/**
 * The words of the handoff panel (plan-018 WAPP-C; design §30.12; ADR 0061; owner answers Q7, Q8): one sentence per
 * line state, per refusal reason and per path refusal, in the portal's language (catalog `channels.{en,el}.json`,
 * namespace `messagingMedia`). Pure apart from `portalText`, so the panel and its tests share one wording.
 *
 * ```
 * a line           sip_trunk  "Your SIP trunk “<name>”"        carrier "wetalk numbers"
 *                  browser    "Voice on your website"          web_chat "Chat on your website"
 * its state        available · not_available_yet · priority_message · inbound_only_trunk → a tag and a note
 * why it is closed switch_off · inbound_only_trunk · no_voice_binding (the API's handoff_unavailable reasons, worked
 *                  out from the rows the same way `apps/api/src/routes/handoff/availability.ts` does)
 * a refusal        handoff_not_offered · handoff_unavailable(reason) · handoff_destination_refused(path_refusal[])
 *                  · handoff_language_unavailable(reason) · revision_stale · the shape refusals checked before sending
 * ```
 *
 * HONEST BY CONSTRUCTION. While a line is `not_available_yet` (production today: both platform switches off) its note
 * says callers who ask for a person are offered to leave a message — never that a person will be rung. The words never
 * name the carrier, the model or the model backup.
 */
import type { HandoffSettingRefusal } from "@wetalk/domain";

import { portalText } from "../../../../i18n/portal.js";
import type {
  HandoffAvailabilityState,
  HandoffAvailabilityView,
  HandoffPathRefusalView,
} from "../../../../api/slice/handoff.js";

/** Why switching the handoff on is not possible now (the API's `handoff_unavailable` reasons). */
export type HandoffClosedReason = "switch_off" | "inbound_only_trunk" | "no_voice_binding";

function isVoiceLine(row: HandoffAvailabilityView): boolean {
  return row.path === "sip_trunk" || row.path === "carrier";
}

/**
 * Why the handoff cannot be switched on, or `null` when at least one line that takes calls can put a caller through —
 * the rule the API refuses by, read from the rows it served.
 */
export function handoffClosedReasonOf(availability: readonly HandoffAvailabilityView[]): HandoffClosedReason | null {
  const voice = availability.filter(isVoiceLine);
  if (voice.some((one) => one.state === "available")) return null;
  if (voice.length === 0) return "no_voice_binding";
  if (voice.some((one) => one.state === "not_available_yet")) return "switch_off";
  return "inbound_only_trunk";
}

/** The rows in the order they are drawn: numbers on the customer's own SIP trunks first (Q8), then the rest. */
export function linesSipTrunkFirst(availability: readonly HandoffAvailabilityView[]): readonly HandoffAvailabilityView[] {
  const rank = { sip_trunk: 0, carrier: 1, browser: 2, web_chat: 3 } as const;
  return [...availability].sort((left, right) => rank[left.path] - rank[right.path]);
}

/** A line's name. */
export function lineNameOf(row: Pick<HandoffAvailabilityView, "path" | "sipTrunkName">): string {
  switch (row.path) {
    case "sip_trunk":
      return row.sipTrunkName === null
        ? portalText("messagingMedia", "handoff.line.sip_trunk")
        : portalText("messagingMedia", "handoff.line.sip_trunk_named", { name: row.sipTrunkName });
    case "carrier":
      return portalText("messagingMedia", "handoff.line.carrier");
    case "browser":
      return portalText("messagingMedia", "handoff.line.browser");
    case "web_chat":
      return portalText("messagingMedia", "handoff.line.web_chat");
  }
}

/** The short tag beside a line's name. */
export function lineStateTagOf(state: HandoffAvailabilityState): string {
  switch (state) {
    case "available":
      return portalText("messagingMedia", "handoff.state.available");
    case "not_available_yet":
      return portalText("messagingMedia", "handoff.state.not_available_yet");
    case "priority_message":
      return portalText("messagingMedia", "handoff.state.priority_message");
    case "inbound_only_trunk":
      return portalText("messagingMedia", "handoff.state.inbound_only_trunk");
  }
}

/** The sentence under a line: what a caller on it who asks for a person gets. */
export function lineNoteOf(row: Pick<HandoffAvailabilityView, "path" | "state">): string {
  switch (row.state) {
    case "available":
      return row.path === "sip_trunk"
        ? portalText("messagingMedia", "handoff.note.sip_trunk_available")
        : portalText("messagingMedia", "handoff.note.carrier_available");
    case "not_available_yet":
      return portalText("messagingMedia", "handoff.note.not_available_yet");
    case "inbound_only_trunk":
      return portalText("messagingMedia", "handoff.note.inbound_only_trunk");
    case "priority_message":
      return portalText("messagingMedia", "handoff.note.priority_message");
  }
}

/** The sentence under the switch while it cannot be switched on. */
export function closedReasonTextOf(reason: HandoffClosedReason): string {
  switch (reason) {
    case "switch_off":
      return portalText("messagingMedia", "handoff.closed.switch_off");
    case "inbound_only_trunk":
      return portalText("messagingMedia", "handoff.closed.inbound_only_trunk");
    case "no_voice_binding":
      return portalText("messagingMedia", "handoff.closed.no_voice_binding");
  }
}

/** A shape problem found before sending (the API's `400 validation_failed` rules, `handoffSettingRefusalOf`). */
export function settingProblemTextOf(refusal: HandoffSettingRefusal): string {
  switch (refusal) {
    case "destination_missing":
      return portalText("messagingMedia", "handoff.problem.destination_missing");
    case "destination_not_e164":
      return portalText("messagingMedia", "handoff.problem.destination_not_e164");
    case "language_missing":
      return portalText("messagingMedia", "handoff.problem.language_missing");
  }
}

/** `422 handoff_unavailable`'s `detail.reason`, in words; `null` for a reason this console does not know. */
export function unavailableReasonTextOf(reason: string | null): string | null {
  switch (reason) {
    case "switch_off":
      return portalText("messagingMedia", "handoff.refused.switch_off");
    case "inbound_only_trunk":
      return portalText("messagingMedia", "handoff.refused.inbound_only_trunk");
    case "no_voice_binding":
      return portalText("messagingMedia", "handoff.refused.no_voice_binding");
    default:
      return null;
  }
}

/** `422 handoff_language_unavailable`'s `detail.reason`, in words; `null` for a reason this console does not know. */
export function languageReasonTextOf(reason: string | null): string | null {
  switch (reason) {
    case "not_configured":
      return portalText("messagingMedia", "handoff.refused.language_not_configured");
    case "framing_missing":
      return portalText("messagingMedia", "handoff.refused.language_framing_missing");
    default:
      return null;
  }
}

/** One line's reason for refusing the number (`detail.path_refusal[].refusal`), in words. */
export function pathRefusalReasonTextOf(refusal: HandoffPathRefusalView): string {
  switch (refusal.refusal) {
    case "not_e164":
      return portalText("messagingMedia", "handoff.path_refusal.not_e164");
    case "country_not_allowed":
      return refusal.path === "sip_trunk"
        ? portalText("messagingMedia", "handoff.path_refusal.country_not_allowed_trunk")
        : portalText("messagingMedia", "handoff.path_refusal.country_not_allowed_carrier");
    case "country_unreviewed":
      return portalText("messagingMedia", "handoff.path_refusal.country_unreviewed");
    case "emergency_or_short":
      return portalText("messagingMedia", "handoff.path_refusal.emergency_or_short");
    case "premium_rate":
      return portalText("messagingMedia", "handoff.path_refusal.premium_rate");
    case "trunk_number":
      return portalText("messagingMedia", "handoff.path_refusal.trunk_number");
    case "wetalk_number":
      return portalText("messagingMedia", "handoff.path_refusal.wetalk_number");
    default:
      return portalText("messagingMedia", "handoff.path_refusal.other");
  }
}

/** "<line>: <reason>" for one path refusal, naming the trunk from the rows the panel holds. */
export function pathRefusalTextOf(refusal: HandoffPathRefusalView, availability: readonly HandoffAvailabilityView[]): string {
  const trunk = refusal.sipTrunkId === null ? undefined : availability.find((one) => one.sipTrunkId === refusal.sipTrunkId);
  const line = lineNameOf({ path: refusal.path, sipTrunkName: trunk?.sipTrunkName ?? null });
  return portalText("messagingMedia", "handoff.path_refusal.line", { line, reason: pathRefusalReasonTextOf(refusal) });
}

/** The typed number as it is sent: spaces, dashes, dots and brackets removed; empty stays empty. */
export function destinationOf(typed: string): string {
  return typed.replace(/[\s\-().]/g, "");
}
